import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { auth } from 'config/firebase';
import {
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  updateProfile as firebaseUpdateProfile,
} from 'firebase/auth';
import { reportAuthError } from 'utils/reportError';

const AuthContext = createContext();

/**
 * Resolve the signed-in user's role from their ID token claim.
 *
 * The claim is the same source the RTDB security rules read
 * (`auth.token.role` in database.rules.*.json), so gating the UI on it means the
 * client and the data layer finally agree. Reading the `users/{uid}` row instead
 * would let them disagree — which is exactly the drift #462 cleaned up.
 *
 * Force-refreshed: a role change is written to the claim, but a cached ID token
 * keeps the old value for up to an hour. Without the refresh, a promoted user
 * stays locked out — and, worse, a demoted one keeps their access — until the
 * token happens to expire. Falls back to the cached token if the refresh fails,
 * so a flaky network logs nobody out.
 */
const resolveRole = async (currentUser) => {
  if (!currentUser) return null;

  try {
    const { claims } = await currentUser.getIdTokenResult(true);
    return claims.role ?? null;
  } catch {
    try {
      const { claims } = await currentUser.getIdTokenResult();
      return claims.role ?? null;
    } catch (error) {
      // Both reads failed: a real admin is about to lose their role (#479).
      reportAuthError(error, {
        component: 'AuthContext',
        action: 'resolveRole',
      });
      return null;
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      const nextRole = await resolveRole(currentUser);
      if (!active) return;

      setUser(currentUser);
      setRole(nextRole);

      // Only now. Consumers gate on `loading`, so releasing it before the role
      // is known would flash an authenticated-but-roleless state and bounce a
      // legitimate admin off /admin.
      setLoading(false);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const logout = useCallback(async () => {
    setAuthError(null);
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      reportAuthError(error, { component: 'AuthContext', action: 'logout' });
      setAuthError(error.message);
      return { success: false, error: error.message };
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    setAuthError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      reportAuthError(error, {
        component: 'AuthContext',
        action: 'resetPassword',
      });
      setAuthError(error.message);
      return { success: false, error: error.message };
    }
  }, []);

  const updateProfile = useCallback(
    async (data) => {
      setAuthError(null);
      try {
        if (!user) throw new Error('No user logged in');

        await firebaseUpdateProfile(auth.currentUser, data);

        // Force refresh the user object to get updated profile
        setUser({ ...auth.currentUser });

        return { success: true };
      } catch (error) {
        reportAuthError(error, {
          component: 'AuthContext',
          action: 'updateProfile',
        });
        setAuthError(error.message);
        return { success: false, error: error.message };
      }
    },
    [user],
  );

  // Clear any auth errors when component unmounts
  useEffect(() => {
    return () => setAuthError(null);
  }, []);

  const value = {
    user,
    role,
    loading,
    authError,
    logout,
    resetPassword,
    updateProfile,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
